// ─────────────────────────────────────────────────────────────────
// Glass-i — RFQ frontend helpers (Stage 6RFQ). Pure functions: status
// labels / colours + small formatters. No network, no React.
// ─────────────────────────────────────────────────────────────────

export const RFQ_STATUS_LABEL = {
  DRAFT:               'Draft',
  SENT:                'Sent',
  PARTIALLY_RESPONDED: 'Partial responses',
  RESPONSES_RECEIVED:  'Responses received',
  CONVERTED:           'Converted to PO',
  CANCELLED:           'Cancelled'
};

export const RFQ_STATUS_COLOR = {
  DRAFT:               'default',
  SENT:                'blue',
  PARTIALLY_RESPONDED: 'gold',
  RESPONSES_RECEIVED:  'cyan',
  CONVERTED:           'green',
  CANCELLED:           'red'
};

export const VENDOR_STATUS_LABEL = {
  PENDING:   'Not sent',
  SENT:      'Sent',
  RESPONDED: 'Quoted',
  DECLINED:  'Declined'
};

export const VENDOR_STATUS_COLOR = {
  PENDING:   'default',
  SENT:      'blue',
  RESPONDED: 'green',
  DECLINED:  'red'
};

export function rfqStatusLabel(s) { return RFQ_STATUS_LABEL[s] || s || ''; }
export function rfqStatusColor(s) { return RFQ_STATUS_COLOR[s] || 'default'; }
export function vendorStatusLabel(s) { return VENDOR_STATUS_LABEL[s] || s || ''; }
export function vendorStatusColor(s) { return VENDOR_STATUS_COLOR[s] || 'default'; }

export function canEditRfq(rfq) { return !!rfq && rfq.status === 'DRAFT'; }
export function canSendRfq(rfq) {
  return !!rfq && ['DRAFT', 'SENT', 'PARTIALLY_RESPONDED', 'RESPONSES_RECEIVED'].includes(rfq.status)
    && Array.isArray(rfq.vendors) && rfq.vendors.length > 0;
}
export function canQuoteRfq(rfq) {
  return !!rfq && ['SENT', 'PARTIALLY_RESPONDED', 'RESPONSES_RECEIVED'].includes(rfq.status);
}
export function canAwardRfq(rfq) {
  return !!rfq && ['PARTIALLY_RESPONDED', 'RESPONSES_RECEIVED'].includes(rfq.status)
    && Array.isArray(rfq.vendors) && rfq.vendors.some((v) => v.status === 'RESPONDED');
}
export function canCancelRfq(rfq) {
  return !!rfq && !['CONVERTED', 'CANCELLED'].includes(rfq.status);
}

const _inr = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export function money(n) {
  const v = Number(n);
  return Number.isFinite(v) ? _inr.format(v) : '0.00';
}

// Lifecycle rail mapping for the detail-page Steps. Returns the AntD Steps
// `current` index plus a `status` ('process' | 'finish' | 'error') so a
// cancelled RFQ reads as stalled rather than mid-flight.
export const RFQ_STEP_TITLES = ['Draft', 'Sent to vendors', 'Quotes received', 'Awarded → PO'];
export function rfqStep(rfq) {
  switch (rfq && rfq.status) {
    case 'DRAFT':               return { current: 0, status: 'process' };
    case 'SENT':                return { current: 1, status: 'process' };
    case 'PARTIALLY_RESPONDED': return { current: 2, status: 'process' };
    case 'RESPONSES_RECEIVED':  return { current: 2, status: 'finish' };
    case 'CONVERTED':           return { current: 3, status: 'finish' };
    case 'CANCELLED':           return { current: 1, status: 'error' };
    default:                    return { current: 0, status: 'process' };
  }
}

// Response progress as a 0–100 integer (responded / total vendors).
export function responsePct(responded, total) {
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  return Math.round((Math.min(Number(responded) || 0, t) / t) * 100);
}

// Given responded vendors, return the lowest- and next-lowest-total quotes so
// the bid board can show how much the winning bid saves. Returns null when
// fewer than one responded.
export function lowestQuote(vendors) {
  const responded = (vendors || [])
    .filter((v) => v.status === 'RESPONDED' && Number.isFinite(Number(v.quoteTotal)))
    .sort((a, b) => Number(a.quoteTotal) - Number(b.quoteTotal));
  if (!responded.length) return null;
  const lowest = responded[0];
  const next = responded[1] || null;
  const savings = next ? Number(next.quoteTotal) - Number(lowest.quoteTotal) : 0;
  return { id: lowest.id, total: Number(lowest.quoteTotal), savings, hasRivals: !!next };
}
